import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  isDevMode,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { LobPreferenceDialogComponent } from '@cigna/chcp/shared/ui';
import { UserFacade } from '@cigna/chcp/shared/user-profile-data-access';
import { ChcpPageBg, ChcpPageLayout, RouteData } from '@cigna/chcp/shared/util';
import { ShellFacade } from '@cigna/chcp/shell/data-access';
import { RouterFacade } from '@cigna/shared/angular/core/router-state-data-access';
import {
  FeatureToggleType,
  FeatureTogglesFacade,
} from '@cigna/shared/angular/features-feature';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, combineLatest } from 'rxjs';

import { Entitlement, LOB, LobPref } from '@cigna/chcp/auth/util';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '@cigna/chcp/shared/environment-util';
import {
  filter,
  map,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ChcpContentfulService } from '@cigna/chcp/shared/contentful-data-access';
import { Alertv2 } from '@cigna/chcp/shell/ui';
import { RouteGuard } from '@cigna/chcp/shared/features-util';
import { ChcpLaunchDarklyFlagKey } from '@cigna/chcp/shared/launch-darkly-util';

@Component({
  selector: 'cigna-layout-secure',
  templateUrl: './layout-secure.component.html',
  styleUrls: ['./layout-secure.component.scss'],
})
export class LayoutSecureComponent implements OnInit, OnDestroy {
  firstName$ = this.userFacade.firstName$;
  lastName$ = this.userFacade.lastName$;
  userid$ = this.authFacade.userid$;
  passwordExpiry$ = this.authFacade.passwordExpiry$;
  shouldShowPwBanner$ = this.passwordExpiry$.pipe(
    map((age) => (age && age <= 15) || false),
  );
  hasUndefinedLobPref$ = this.userFacade.hasUndefinedLobPref$;

  onDestroy$ = new Subject<void>();
  impersonator$ = this.authFacade.isImpersonator$;
  siteDownFlag$ =
    this.featureToggleFacade.featuresEnabled<ChcpLaunchDarklyFlagKey>([
      'ftr-chcp-global-team-web-site_down',
    ]);
  isEnhInvUserFlow$ =
    this.featureToggleFacade.featuresEnabled<ChcpLaunchDarklyFlagKey>([
      'ftr-chcp-nova-team-web-new-manage-user-invite-flow',
    ]);

  isChatAllowedImpersonator$ = combineLatest([
    this.authFacade.isImpersonator$,
    this.authFacade.isChatAllowedImpersonator$,
    this.authFacade.isUserAuthenticated$,
  ])
    .pipe(
      filter(
        ([_isImpersonator, _isChatAllowedImpersonator, isUserAuthenticated]) =>
          isUserAuthenticated,
      ),
      take(1),
      takeUntil(this.onDestroy$),
    )
    .subscribe(
      ([isImpersonator, isChatAllowedImpersonator, _isUserAuthenticated]) => {
        this.featureToggleFacade.updateFeatureToggles(
          {
            isNotImpersonator: !isImpersonator || isChatAllowedImpersonator,
          },
          FeatureToggleType.PERMISSION,
        );
        return isChatAllowedImpersonator;
      },
    );

  isMedicalUser$ = this.authFacade.isMedicalUser$;

  profileNav$ = this.shellFacade.profileNav$;
  profileNavError$ = this.shellFacade.profileNavError$;
  siteNav$ = this.shellFacade.siteNav$;
  siteNavError$ = this.shellFacade.siteNavError$;
  messageCenterFunction$ = this.authFacade.hasMessageCenterCapability$;

  routeData$ = this.routerFacade.data$ as Observable<RouteData>;
  pageLayout$: Observable<ChcpPageLayout> = this.routeData$.pipe(
    map((data) => data.pageLayout ?? 'default'),
  );
  pageBg$: Observable<ChcpPageBg> = this.routeData$.pipe(
    map((data) => data.pageBg ?? 'default'),
  );
  routeUrl$ = this.routerFacade.url$;

  isResourcePage$ = this.routeData$.pipe(
    map((data) => data.routeCode === 'resources'),
  );
  logoLink$ = combineLatest([this.isResourcePage$, this.isMedicalUser$]).pipe(
    map(([isResourcePage, isMedicalUser]) =>
      isResourcePage
        ? isMedicalUser
          ? `${this.envConfig.redirect.resourceLibrary}assets/chcp/medical/resourceLibrary/resourceLibraryMed.html`
          : `${this.envConfig.redirect.resourceLibrary}assets/chcp/dental/resourceLibrary/resourceLibraryDent.html`
        : '/dashboard',
    ),
  );

  isRouteGuardLoading$ = this.routeGuard.loader$;

  secureAlerts: Alertv2[] = [];

  // eslint-disable-next-line @typescript-eslint/naming-convention
  inBrowserPlatform: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  DEV_MODE = false;
  isApiMock: boolean;

  hasWamApproval$ = this.shellFacade.hasWamApproval$;
  hasDelegation$ = this.authFacade.hasOneOfEntitlements$([
    Entitlement.DelegationAccessAndViews,
  ]);

  constructor(
    public shellFacade: ShellFacade,
    private userFacade: UserFacade,
    private authFacade: AuthFacade,
    private routerFacade: RouterFacade,
    private cookieService: CookieService,
    private featureToggleFacade: FeatureTogglesFacade,
    private contentfulService: ChcpContentfulService,
    public dialog: MatDialog,
    private routeGuard: RouteGuard,
    @Inject(PLATFORM_ID) platformId: {},
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {
    this.inBrowserPlatform = isPlatformBrowser(platformId);
    this.isApiMock = this.envConfig.apiMock;
  }

  ngOnInit() {
    this.featureToggleFacade.updateFeatureToggles(
      {
        isUserAuthorized: true,
      },
      FeatureToggleType.PERMISSION,
    );

    /* FOR LOCAL DEV ONLY */
    if (isDevMode() && this.isApiMock) {
      this.DEV_MODE = true;
    }

    this.shellFacade.loadNav();

    combineLatest([this.authFacade.lob$])
      .pipe(
        filter(([lob]) => lob !== undefined && lob !== null),
        take(1),
        withLatestFrom(this.authFacade.entitlements$),
      )
      .subscribe(([[lob], entitlements]) => {
        this.contentfulService
          .getAlerts('4R5tpJpbqNMI463t3w2cB1', lob as LOB, entitlements)
          .then((alerts) => (this.secureAlerts = alerts))
          .catch(() => {
            this.secureAlerts = [];
          });
      });

    // remove deeplink cookie after login unless we are in the middle of mfa flow
    this.routeUrl$
      .pipe(
        filter((url) => !url?.startsWith('/mfa')),
        take(1),
        tap(() => {
          this.cookieService.delete('CHCP_DEEPLINK', '/');
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();

    this.hasUndefinedLobPref$
      .pipe(take(2), withLatestFrom(this.authFacade.isImpersonator$))
      .subscribe(([hasUndefinedLobPref, isImpersonator]) => {
        if (hasUndefinedLobPref && !isImpersonator) {
          this.dialog
            .open(LobPreferenceDialogComponent, {
              disableClose: true,
              autoFocus: false,
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(({ lobPref }: { lobPref: LobPref }) => {
              if (lobPref) {
                this.userFacade.updateProfile({ lobPref });
              }
            });
        }
      });
    this.loadWamApprovalIfEligible();
  }

  loadWamApprovalIfEligible() {
    this.hasDelegation$
      .pipe(
        filter((hasWamEntitlement) => hasWamEntitlement),
        take(1),
        withLatestFrom(this.isEnhInvUserFlow$),
      )
      .subscribe(([hasWamEntitlement, isEnhInvUserFlow]) => {
        if (hasWamEntitlement && isEnhInvUserFlow) {
          this.shellFacade.loadWamApprovalStatus();
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
